import React, {ChangeEvent} from 'react';
import {Box, Button, InputBase, Typography} from '@mui/material';
import {styled} from '@mui/material/styles';
import TextFieldsIcon from '@mui/icons-material/TextFields';
import AddPhotoAlternateIcon from '@mui/icons-material/AddPhotoAlternate';
import LayersIcon from '@mui/icons-material/Layers';
import {useTranslation} from 'react-i18next';
import {ImageValue} from '../../../models/Template';
import {Scene} from '../../../scene/types';
import {layerSubtitle, layerTitle, UserLayer, UserLayerStyle} from '../../../scene/userLayers';
import {LayerTextStyleControls, LayerZOrderControls} from '../layers/LayerControls';

const SectionHeader = styled(Box)({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '12px 0',
    color: 'white',
    borderBottom: '1px solid #ffffff70',
    gap: 8,
});

const LayerCard = styled(Box, {
    shouldForwardProp: (prop) => prop !== 'selected',
})<{ selected?: boolean }>(({theme, selected}) => ({
    border: selected ? '1px solid #00E162' : '1px solid #ffffff40',
    boxShadow: selected ? '0 0 0 2px #00E16266' : 'none',
    transition: 'border-color 0.2s ease, box-shadow 0.2s ease',
    borderRadius: '12px',
    padding: theme.spacing(1.5),
    marginTop: theme.spacing(1.5),
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
}));

const AddButton = styled(Button)({
    color: '#C3F3FD',
    borderColor: '#ffffff60',
    textTransform: 'none',
    borderRadius: '10px',
    fontSize: '0.8rem',
});

const linkSx = {
    background: 'transparent',
    border: 'none',
    color: '#C3F3FD',
    cursor: 'pointer',
    fontSize: '0.8rem',
    padding: '4px 2px',
    textDecoration: 'underline',
} as const;

const Thumb = styled('img')({
    width: 48,
    height: 48,
    borderRadius: 8,
    objectFit: 'cover',
    border: '1px solid #ffffff50',
    flexShrink: 0,
});

interface UserLayersPanelProps {
    layers: UserLayer[];
    selections: { [key: string]: string | ImageValue };
    /** The MERGED scene — the style controls resolve an INHERITED colour off it. */
    scene?: Scene;
    /** Canvas selection, so picking a layer on the board highlights its card
     *  (and scrolls it into view). */
    selectedId?: string | null;
    /** validSlots(design.scene) — legal `order` values. */
    slots: number[];
    /** The input key currently in the reposition (Konva Transformer) flow. */
    repositioningKey: string | undefined;
    /** False when the template exposes no image folder — there is no end-user
     *  folder-list endpoint, so an image layer would have nowhere to pick from. */
    canAddImage: boolean;
    onAddText: () => void;
    onAddImage: () => void;
    onTextChange: (event: ChangeEvent<HTMLTextAreaElement | HTMLInputElement>, key: string) => void;
    onFontStep: (key: string, direction: 1 | -1) => void;
    onStyle: (id: string, style: Partial<UserLayerStyle>) => void;
    onMove: (id: string, move: 'up' | 'down' | 'front' | 'back') => void;
    onDelete: (id: string) => void;
    onReplaceImage: (id: string) => void;
    onReposition: (key: string | undefined) => void;
}

/** DESKTOP: the user's own layers, below the template's field accordions. Each
 *  card carries exactly what that layer kind can be edited by — text gets the
 *  value, font steppers, colour and alignment; an image gets replace — plus the
 *  shared z-order + delete controls. */
const UserLayersPanel: React.FC<UserLayersPanelProps> = ({
                                                             layers,
                                                             selections,
                                                             scene,
                                                             selectedId,
                                                             slots,
                                                             repositioningKey,
                                                             canAddImage,
                                                             onAddText,
                                                             onAddImage,
                                                             onTextChange,
                                                             onFontStep,
                                                             onStyle,
                                                             onMove,
                                                             onDelete,
                                                             onReplaceImage,
                                                             onReposition,
                                                         }) => {
    const {t} = useTranslation();
    const selectedRef = React.useRef<HTMLDivElement>(null);

    // Selecting on the canvas should bring the matching card to the user, not
    // make them hunt for it in a ten-card list.
    React.useEffect(() => {
        if (!selectedId) return;
        // Optional-call: scrollIntoView is missing in jsdom and in some embedded
        // webviews, and an exception here unmounts the whole panel.
        selectedRef.current?.scrollIntoView?.({block: 'nearest', behavior: 'smooth'});
    }, [selectedId]);

    return (
        <Box data-testid="user-layers-panel">
            <SectionHeader>
                <Box sx={{display: 'flex', alignItems: 'center'}}>
                    <LayersIcon sx={{marginRight: 1}}/>
                    <Typography variant="subtitle1">{t('editor.layers.myLayer')}</Typography>
                </Box>
                <Box sx={{display: 'flex', gap: 1}}>
                    <AddButton
                        variant="outlined"
                        size="small"
                        startIcon={<TextFieldsIcon/>}
                        onClick={onAddText}
                        data-testid="add-text-layer"
                    >
                        {t('editor.layers.addText')}
                    </AddButton>
                    {canAddImage && (
                        <AddButton
                            variant="outlined"
                            size="small"
                            startIcon={<AddPhotoAlternateIcon/>}
                            onClick={onAddImage}
                            data-testid="add-image-layer"
                        >
                            {t('editor.layers.addImage')}
                        </AddButton>
                    )}
                </Box>
            </SectionHeader>

            {/* The chips on the board are purple for layers and blue/green for
                template fields; say so once rather than leaving it to be guessed. */}
            <Typography variant="caption" data-testid="layers-purple-legend"
                        sx={{color: '#8fb0c8', display: 'block', mt: 1}}>
                {t('editor.layers.purpleLegend')}
            </Typography>

            {/* Top of the stack first — the card list reads like the board. */}
            {[...layers].reverse().map((layer) => {
                const value = selections[layer.id];
                const repositioning = repositioningKey === layer.id;
                const selected = selectedId === layer.id;
                return (
                    <LayerCard
                        key={layer.id}
                        ref={selected ? selectedRef : undefined}
                        selected={selected}
                        data-selected={selected || undefined}
                        data-testid={`layer-card-${layer.id}`}
                    >
                        <Box sx={{display: 'flex', alignItems: 'center', gap: 1}}>
                            <Box sx={{flexGrow: 1, minWidth: 0}}>
                                <Typography sx={{color: 'white', fontWeight: 600, fontSize: '0.9rem'}} noWrap>
                                    {layerTitle(layers, layer,
                                        t(layer.kind === 'text' ? 'editor.layers.textLayer' : 'editor.layers.imageLayer'))}
                                </Typography>
                                {/* LIVE content, not the creation-time default. */}
                                <Typography dir="auto" noWrap data-testid={`layer-subtitle-${layer.id}`}
                                            sx={{color: '#8fb0c8', fontSize: '0.78rem'}}>
                                    {layerSubtitle(layer, value as any,
                                        t('editor.layers.emptyText'), t('editor.layers.replaceImage'))}
                                </Typography>
                            </Box>
                            <Box
                                component="button"
                                type="button"
                                sx={linkSx}
                                data-testid={`layer-reposition-${layer.id}`}
                                onClick={() => onReposition(repositioning ? undefined : layer.id)}
                            >
                                {t(repositioning ? 'editor.imageSelector.confirm' : 'editor.imageSelector.reposition')}
                            </Box>
                        </Box>

                        {layer.kind === 'text' ? (
                            <>
                                <InputBase
                                    fullWidth
                                    multiline
                                    value={(value as string) ?? ''}
                                    onChange={(event) => onTextChange(event, layer.id)}
                                    inputProps={{
                                        dir: 'auto',
                                        'aria-label': t('editor.layers.textValue'),
                                        'data-testid': `layer-text-${layer.id}`,
                                    }}
                                    sx={{
                                        '& .MuiInputBase-input': {
                                            backgroundColor: '#071826',
                                            border: '1px solid #2ea6e6',
                                            borderRadius: '10px',
                                            color: '#fff',
                                            fontSize: '0.95rem',
                                            padding: '8px 12px',
                                        },
                                    }}
                                />
                                <Box sx={{display: 'flex', gap: 1}}>
                                    <Box
                                        component="button"
                                        type="button"
                                        sx={linkSx}
                                        data-testid={`layer-font-minus-${layer.id}`}
                                        onClick={() => onFontStep(layer.id, -1)}
                                    >
                                        {t('editor.imageSelector.fontDecrease')}
                                    </Box>
                                    <Box
                                        component="button"
                                        type="button"
                                        sx={linkSx}
                                        data-testid={`layer-font-plus-${layer.id}`}
                                        onClick={() => onFontStep(layer.id, 1)}
                                    >
                                        {t('editor.imageSelector.fontIncrease')}
                                    </Box>
                                </Box>
                                <LayerTextStyleControls layer={layer} scene={scene} onStyle={onStyle}/>
                            </>
                        ) : (
                            <Box sx={{display: 'flex', alignItems: 'center', gap: 1.5}}>
                                {(value as ImageValue)?.thumbnail && (
                                    <Thumb src={(value as ImageValue).thumbnail} alt="" loading="lazy"/>
                                )}
                                <Box
                                    component="button"
                                    type="button"
                                    sx={linkSx}
                                    data-testid={`layer-replace-${layer.id}`}
                                    onClick={() => onReplaceImage(layer.id)}
                                >
                                    {t('editor.layers.replaceImage')}
                                </Box>
                            </Box>
                        )}

                        <LayerZOrderControls
                            layer={layer}
                            layers={layers}
                            slots={slots}
                            onMove={onMove}
                            onDelete={onDelete}
                        />
                    </LayerCard>
                );
            })}
        </Box>
    );
};

export default UserLayersPanel;
